import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HeartHandshake, TrendingUp, Users, GraduationCap, ShieldCheck,
  MapPin, ArrowRight, Briefcase, HandHeart, Lightbulb, BadgeCheck,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SITE } from "../data/siteData";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

const CULTURE = [
  { icon: HeartHandshake, title: "Ethics Before Everything", text: "We grew as Eastern India's first branded oncology house by doing things the right way. Transparent, patient-centric thinking guides every decision — from pricing to promotion." },
  { icon: HandHeart, title: "Purpose You Can Feel", text: "Our medicines reach cancer patients, families and communities that depend on affordable care. Every role here, from field to office, touches a real life." },
  { icon: Users, title: "A Close-Knit Team", text: "We are a growing Kolkata-headquartered family, not a faceless corporation. Your work is visible, your voice is heard, and your contribution matters from day one." },
  { icon: ShieldCheck, title: "Integrity in the Field", text: "Ethical marketing is one of our three pillars. We equip our teams to win trust through science, evidence and honest communication — never pressure." },
];

const EXPECT = [
  { icon: TrendingUp, title: "Real Growth", text: "As we expand toward Vision 2030 — new segments like cardio-diabetic, dermatology, geriatric and palliative care — new roles and territories open up constantly. Grow as we grow." },
  { icon: GraduationCap, title: "Learning & Development", text: "Structured product training on oncology, biosimilars and speciality formulations, plus mentorship from seasoned pharma professionals." },
  { icon: Lightbulb, title: "Ownership & Ideas", text: "Have an idea for a territory, a segment or a process? Bring it. We reward initiative and back people who take ownership." },
  { icon: BadgeCheck, title: "Stability with Ambition", text: "Nearly two decades of steady, profitable growth — combined with the energy of a company still writing its biggest chapters." },
];

const ROLES = [
  { title: "Medical Representative", location: "Kolkata & Eastern India territories", type: "Field" },
  { title: "Area Business Manager", location: "West Bengal, Odisha, Bihar, North East", type: "Field Leadership" },
  { title: "Product Management Executive", location: "Kolkata HQ", type: "Office" },
  { title: "Distribution & Supply Chain Coordinator", location: "Kolkata HQ", type: "Office" },
];

const Careers = () => (
  <div data-testid="careers-page">
    <PageHeader
      testId="careers-header"
      eyebrow="Careers at Cytocare"
      title="Build your career where care is the business"
      subtitle="Join the team that put branded oncology care on the map in Eastern India — and is now growing towards a pan-India presence by 2030."
    />

    {/* Culture */}
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="careers-culture">
      <motion.div {...fadeUp} className="max-w-2xl">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Our Culture</p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          How we work, and what we stand for
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          {SITE.tagline} is not just our tagline — it is how we treat our patients, our partners
          and our people. Since {SITE.founded}, our growth has been driven by integrity and a
          commitment to transparent, patient-centric healthcare.
        </p>
      </motion.div>
      <div className="mt-12 grid gap-6 sm:grid-cols-2">
        {CULTURE.map((c, i) => (
          <motion.div
            key={c.title}
            {...fadeUp}
            transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
            className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-xl"
            data-testid={`culture-card-${i}`}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8]">
              <c.icon size={22} />
            </div>
            <h3 className="mt-5 text-lg font-bold text-slate-900">{c.title}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{c.text}</p>
          </motion.div>
        ))}
      </div>
    </section>

    {/* What to expect */}
    <section className="bg-[#F2F7FB] py-20" data-testid="careers-expect">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">What to Expect</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            What you get when you join us
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {EXPECT.map((e, i) => (
            <motion.div
              key={e.title}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm"
              data-testid={`expect-card-${i}`}
            >
              <e.icon size={22} className="text-[#1D4ED8]" />
              <h3 className="mt-4 text-base font-bold text-slate-900">{e.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{e.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Open roles */}
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="careers-roles">
      <motion.div {...fadeUp} className="max-w-2xl">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Open Roles</p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Positions we are hiring for
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Don't see your exact role? We are always glad to meet talented people who share our
          values — write to us anyway.
        </p>
      </motion.div>
      <div className="mt-10 space-y-4">
        {ROLES.map((r, i) => (
          <motion.div
            key={r.title}
            {...fadeUp}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-7 py-6 shadow-sm transition-shadow duration-300 hover:shadow-lg"
            data-testid={`role-card-${i}`}
          >
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8]">
                <Briefcase size={19} />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{r.title}</h3>
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                  <MapPin size={12} /> {r.location}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-[#1D4ED8]">{r.type}</span>
              <Link
                to="/contact"
                data-testid={`role-apply-${i}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4ED8] transition-colors hover:text-[#173CAE]"
              >
                Apply <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="bg-[#0A1633] py-20 text-white" data-testid="careers-cta">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Ready to grow with us?</h2>
          <p className="mt-3 text-slate-300">
            Send your profile to <span className="font-semibold text-white">{SITE.email}</span> with
            the role in the subject line.
          </p>
        </div>
        <Link
          to="/contact"
          data-testid="careers-contact-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#3B82F6]"
        >
          Get in touch <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  </div>
);

export default Careers;
